import { MbStarsRating, MbIcon, MbTooltipTrigger, MbBadge, MbAdvice } from '@am/moebius';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { EventItemParamsMapa } from '@core/models/ga4/interfaces/item_params.interfeace';
import { AlliancesService } from '@core/services/alliances.service';
import { AnalyticsService } from '@core/services/analytics.service';
import { Ga4Service } from '@core/services/ga4.service';
import { PriceService } from '@core/services/price.service';
import { UrlService } from '@services/url.service';
import { CurrencyCode, CurrencyHelper } from '@core/enums';
import { PricingExpandableComponent } from 'app/components/pricing-expandable/pricing-expandable.component';
import { CurrencyPipe } from '@shared/pipes';
import { PricingExpandableComponent as PricingExpandableComponent_1 } from '../../pricing-expandable/pricing-expandable.component';
import { NgClass, SlicePipe } from '@angular/common';
import { AmRewardsPricingComponent } from '@am/rewards-pricing';

@Component({
  selector: 'am-hotels-map-info-window',
  styleUrls: ['./hotels-map-info-window.component.scss'],
  templateUrl: './hotels-map-info-window.component.html',
  imports: [
    MbStarsRating,
    MbIcon,
    MbTooltipTrigger,
    MbBadge,
    NgClass,
    MbAdvice,
    PricingExpandableComponent_1,
    SlicePipe,
    CurrencyPipe,
    AmRewardsPricingComponent,
  ],
})
export class HotelsMapInfoWindowComponent implements OnInit, OnChanges {
  @Input() public data;
  @Input() public query;
  @Input() public brand: string;
  @Output() public closeclick: EventEmitter<void> = new EventEmitter<void>();
  public detailUrl: string;
  public hotelSelected: any;
  public isAlliance: boolean;
  public isArgentinian: boolean;
  public priceToDisplay: number;
  public priceByValue;
  public currencyLabel = CurrencyHelper.getCurrencySymbol(CurrencyCode.ARS);
  public currencyCode: string;
  private brands: string[] = ['avantrip'];
  public brandImage: string;
  @ViewChild(MbStarsRating, { static: false }) public starsRating: MbStarsRating;
  @ViewChild('pricingExpandable', { static: false })
  public breakdownExpandible: PricingExpandableComponent;
  public pricingKey: string;

  public MEAL_PLAN_ICON_MAP: Record<string, string> = {
    FULL_BOARD: 'full-board',
    ALL_INCLUSIVE: 'all-inclusive',

    CONTINENTAL_BREAKFAST: 'breakfast',
    FULL_BREAKFAST: 'breakfast',
    BREAKFAST_BUFFET: 'breakfast',
    CONTINENTAL_BREAKFAST_FOR_2: 'breakfast',
    BREAKFAST_FOR_2: 'breakfast',
    BED_AND_BREAKFAST: 'breakfast',
    BREAKFAST: 'breakfast',

    HALF_BOARD_MORE_DRINKS: 'mealplan',
    HALF_BOARD: 'mealplan',
    FREE_DINNER: 'mealplan',
    FREE_LUNCH: 'mealplan',

    DISNEY_DELUXE: 'mealplan',
    DISNEY_DINING: 'mealplan',
    DISNEY_QUICK_DINING: 'mealplan',
  };

  constructor(
    private urlService: UrlService,
    private priceService: PriceService,
    private allianceService: AlliancesService,
    private analyticsService: AnalyticsService,
    private ga4Service: Ga4Service,
  ) {}

  @Input()
  public set currency(value: string) {
    this.currencyLabel = CurrencyHelper.getCurrencySymbol(CurrencyHelper.toEnumMember(value));
  }

  public ngOnInit(): void {
    this.hotelSelected = this.data;
    this.isAlliance = this.allianceService.isAlliance();
    this.currencyCode = this.hotelSelected?.currency;
    this.isArgentinian = this.hotelSelected?.country === '1123215';
    this.pricingKey = this.hotelSelected?.rate_id;

    if (this.brands.includes(this.brand)) {
      this.brandImage = this.brand;
    } else {
      this.brandImage = 'almundo';
    }
  }

  public ngOnChanges() {
    this.hotelSelected = this.data;
    this.pricingKey = this.hotelSelected?.rate_id;

    this.refreshStarRating();
    this.priceByValue = this.data.priceByValue;
    this.priceToDisplay = Math.ceil(this.hotelSelected.price);
    this.getDetailUrl();
    if (this.breakdownExpandible) {
      this.breakdownExpandible.resetBreakdown();
    }
  }

  public trackByItems(index) {
    return index;
  }

  public refreshStarRating() {
    if (this.starsRating) {
      this.starsRating.ngOnInit();
    }
  }

  public getPrice(hotelSelected) {
    return this.priceService.getMapPrice(hotelSelected, this.hotelSelected?.priceByValue);
  }

  public trackEvent() {
    this.trankingEvent();
    this.analyticsService.trackEvent({
      action: 'Click Map',
      label: 'Open',
      section: 'Results',
    });
    this.analyticsService.trackEvent({
      action: 'Map selected',
      label: this.hotelSelected.isHomestays ? 'Alquiler selected' : 'Hotel selected',
      section: 'Results',
    });
  }

  private getDetailUrl() {
    const { url } = this.urlService.getDetailRedirection(this.hotelSelected);
    this.detailUrl = url;
  }

  public getTaxes() {
    return !(this.query.queryParams.withFullPrice === 'false');
  }

  private trankingEvent() {
    if (this.ga4Service !== undefined) {
      this.ga4Service.select_item<EventItemParamsMapa>(this.hotelSelected, 'mapa');
    }
  }

  getMealPlanIcon(code?: string): string | null {
    if (!code || code === 'ROOM_ONLY') {
      return null;
    }
    return this.MEAL_PLAN_ICON_MAP[code] || null;
  }

  getPaymentBadgeText(hotel: any): string {
    const paymentCode = hotel?.payment_methods?.[0]?.code;
    if (!paymentCode) {
      return '';
    }

    const isUsd = this.currencyCode === 'USD';

    switch (paymentCode) {
      case 'PREPAID':
        if (isUsd) {
          return 'Pago en dólares';
        }
        return this.isArgentinian ? 'Pagar ahora en cuotas' : 'Pagá ahora en 1 cuota en pesos';

      case 'PAY_AT_DESTINATION':
        return 'Pagar al alojamiento';

      default:
        return this.isArgentinian
          ? 'Pagar ahora en cuotas o al alojamiento'
          : 'Pagá ahora en 1 cuota en pesos o al alojamiento';
    }
  }

  getTaxInfoText(): string {
    if (this.isArgentinian || (!this.isArgentinian && this.currencyCode !== 'ARS')) {
      return 'Impuestos incluidos';
    }

    if (this.hotelSelected?.payment_methods?.[0]?.code === 'PAY_AT_DESTINATION') {
      return 'Podría cobrarse la percepción RG5617';
    }

    return 'No se te cobrará la percepción RG5617';
  }

  public expandBreakdown() {
    this.breakdownExpandible.expand();
  }
}
